import { useState } from 'react';
import { Link, createFileRoute, useRouter } from '@tanstack/react-router';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  FilterSelect,
  PageBody,
  TableActionsMenu,
  Toast,
  buttonVariants,
  type DataTableColumn,
  type DataTableSort,
  type FilterOption,
} from '@wundo/ui';
import { UserCircle } from 'lucide-react';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import type { User } from '@/types/users.ts';
import { EmailWithBreak } from '@/components/EmailWithBreak.tsx';
import { PageHeader } from '@/components/PageHeader.tsx';
import { useActiveOrganisation } from '@/lib/activeOrganisation.ts';
import { startImpersonation } from '@/lib/impersonation.ts';
import { listUsers, type UserListParams } from '@/lib/users.ts';
import { CHANNEL_LABELS } from '@/components/users/userLabels.ts';
import { formatNotificationTime } from '@/utils/dateUtils.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';

export const Route = createFileRoute('/_app/users/')({
  staticData: { orgScope: 'required' },
  component: UsersPage,
});

const ROLE_FILTER: FilterOption<string>[] = [
  { value: '', label: 'All roles' },
  { value: 'superadmin', label: 'Superadmin' },
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'User' },
];

const CHANNEL_FILTER: FilterOption<string>[] = [
  { value: '', label: 'All channels' },
  { value: 'web', label: 'Web' },
  { value: 'msteams', label: 'MS Teams' },
  { value: 'slack', label: 'Slack' },
];

const STATUS_FILTER: FilterOption<string>[] = [
  { value: '', label: 'All statuses' },
  { value: 'onboarded', label: 'Onboarded' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'expired', label: 'Expired' },
];

function personName(user: User): string {
  return `${user.first_name || ''} ${user.last_name || ''}`.trim();
}

const columns: DataTableColumn<User>[] = [
  {
    key: 'first_name',
    header: 'Name',
    sortable: true,
    /* The name is the superadmin's own, so "Guest" beside it says whose guest. */
    render: (user) =>
      user.guest_of_user_id ? (
        <span className='inline-flex items-center gap-2'>
          {personName(user)}
          <Badge tone='neutral'>Guest</Badge>
        </span>
      ) : (
        personName(user)
      ),
    mobileSubline: (user) => <EmailWithBreak email={user.email} />,
  },
  { key: 'email', header: 'Email', sortable: true, hideBelow: 'mobile' },
  { key: 'phone', header: 'Phone', sortable: true, hideBelow: 'mobile' },
  {
    key: 'role',
    header: 'Role',
    sortable: true,
    render: (user) => user.role.charAt(0).toUpperCase() + user.role.slice(1),
  },
  {
    key: 'channel',
    header: 'Channel',
    hideBelow: 'mobile',
    sortable: true,
    render: (user) => CHANNEL_LABELS[user.channel] ?? 'Web',
  },
  { key: 'status', header: 'Status', sortable: true },
  {
    key: 'notification_time',
    header: 'Notification Time',
    hideBelow: 'mobile',
    sortable: true,
    render: (user) => formatNotificationTime(user.notification_time),
  },
  {
    key: 'created_at',
    header: 'Created At',
    hideBelow: 'mobile',
    sortable: true,
    render: (user) => new Date(user.created_at).toLocaleString(),
  },
];

/**
 * Everyone in the organisation this session belongs to.
 *
 * Searching, filtering, sorting and paging are all the server's: the list runs
 * into five figures, so every one of them is a parameter on the request rather
 * than a pass over what happens to be on screen.
 */
export function UsersPage() {
  const router = useRouter();
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState<DataTableSort>({
    key: 'created_at',
    direction: 'desc',
  });

  /* Every list on the console is read through the organisation it is looking at. */
  const { organisation } = useActiveOrganisation();
  const organisationId = organisation?.id;

  const params: UserListParams = {
    page,
    limit: PAGE_SIZE,
    organisationId,
    query: search || undefined,
    role: role || undefined,
    channel: channel || undefined,
    status: status || undefined,
    sortBy: sort.key,
    sortOrder: sort.direction,
  };

  const { data, isPending } = useQuery({
    queryKey: ['users', params],
    queryFn: () => listUsers(params),
    enabled: currentUser !== undefined,
    /* The table dims rather than empties between pages, so keep the old rows. */
    placeholderData: keepPreviousData,
    throwOnError: true,
  });

  const hasActiveFilters = Boolean(role || channel || status || search);

  /*
   * Only superadmins may impersonate, and no superadmin or guest account may
   * be impersonated this way — both are enforced by the backend, so the menu
   * simply does not offer what would be refused. Without it there is no actions column at all.
   */
  const canImpersonate = currentUser?.role === 'superadmin';

  const loginAs = useMutation({ mutationFn: startImpersonation });

  /* Every filter narrows the whole list, so a narrowed list starts at its first page. */
  function applyFilter(set: (value: string) => void) {
    return (value: string) => {
      set(value);
      setPage(1);
    };
  }

  function clearFilters() {
    setSearch('');
    setRole('');
    setChannel('');
    setStatus('');
    setPage(1);
  }

  return (
    <>
      <PageHeader title='Users' subtitle='Manage user accounts and permissions' />

      <PageBody>
        <DataTable<User>
          label='Users'
          columns={columns}
          rows={data?.data ?? []}
          rowKey={(user) => user.id}
          loading={isPending}
          sort={sort}
          onSortChange={(next) => {
            setSort(next);
            setPage(1);
          }}
          pagination={{ page, pageSize: PAGE_SIZE, total: data?.meta.totalItems ?? 0 }}
          onPageChange={setPage}
          search={{
            value: search,
            onChange: applyFilter(setSearch),
            placeholder: 'Search users...',
          }}
          toolbar={
            <>
              <FilterSelect
                hideLabel
                label='Role'
                options={ROLE_FILTER}
                value={role}
                onValueChange={applyFilter(setRole)}
              />
              <FilterSelect
                hideLabel
                label='Channel'
                options={CHANNEL_FILTER}
                value={channel}
                onValueChange={applyFilter(setChannel)}
              />
              <FilterSelect
                hideLabel
                label='Status'
                options={STATUS_FILTER}
                value={status}
                onValueChange={applyFilter(setStatus)}
              />
              {hasActiveFilters && (
                <Button variant='quiet' size='sm' onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
              {/* A real link: staff open a create form in a new tab like any other page. */}
              <Link
                to='/users/create'
                className={buttonVariants({ variant: 'accent', size: 'sm' })}
              >
                Create
              </Link>
            </>
          }
          rowHref={(user) => `/users/${user.id}`}
          onNavigate={(href) => void router.navigate({ href })}
          actions={
            canImpersonate
              ? (user) => (
                  <TableActionsMenu
                    label={`Actions for ${personName(user)}`}
                    items={
                      user.role === 'superadmin' || user.guest_of_user_id
                        ? []
                        : [
                            {
                              label: 'Login as',
                              icon: <UserCircle className='size-4' aria-hidden='true' />,
                              onSelect: () => loginAs.mutate(user.id),
                            },
                          ]
                    }
                  />
                )
              : undefined
          }
          emptyState={<EmptyState title='No users found' />}
        />
      </PageBody>

      {/* "Login as" reloads the page when it works, so this only ever says it did not. */}
      <Toast open={loginAs.isError} message='Failed to sign in as this user' />
    </>
  );
}
